import { useToast } from '@chakra-ui/react'
import {
  createContext,
  FC,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react'
import { useImmer } from 'use-immer'

import { parseConfigTolerancia } from '@helper/parametrization/parseConfigPurchase'
import { decrypterHeaderStorange } from '@helper/util/cryptoStorange'
import ParametroCompra from '@service/ParametroCompra'

import { ITableGridAction } from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import { IConfigToleranciaParamProvider } from '@base/@types/providers/Parametrization/ConfigParamTolerancia'

const ParametrizationConfigToleranciaProvider: FC = ({ children }) => {
  const toast = useToast()
  const [loading] = useState(false)
  const [companieId, setCompanieId] = useState('')

  // Tolerancia specific states
  const [dataTolerancias, updateDataTolerancias] = useImmer<any[]>([])
  const [loadingTolerancias, setLoadingTolerancias] = useState(false)
  const [updateToleranciaOb, setUpdateToleranciaOb] = useImmer<any>(null)
  const [selectedTolerancia, setSelectedTolerancia] = useState(0)
  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState('')
  const [fetching] = useState(false)

  const [pagination, updatePagination] = useImmer<any>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })

  const handleStorange = useCallback(async () => {
    // This function might need to be adapted or removed if not relevant for Tolerancia
    // For now, keeping a placeholder based on ConfigPurchaseParamProvider
    if (localStorage.getItem('parametrizationCurveStoretHeader')) {
      decrypterHeaderStorange('parametrizationCurveStoretHeader')
      // Assuming some header logic here, adapt as needed for Tolerancia
      // For now, just a placeholder
      // updateHeaders(newHeader)
    }
  }, [])

  // Descarta respostas antigas quando a empresa troca rápido
  const requestSeq = useRef(0)

  const getDataTolerancias = useCallback(
    async (id: any) => {
      const seq = ++requestSeq.current
      setLoadingTolerancias(true)
      setCompanieId(id)

      handleStorange()

      try {
        ParametroCompra.getListaTolerancias(id)
          .then(({ data: { data } }) => {
            if (seq !== requestSeq.current) return
            updateDataTolerancias(draft => {
              return draft.filter(item => item.status === 99).concat(data)
            })
            setCompanieId(id)
            updatePagination(pagination)
            setLoadingTolerancias(false)
          })
          .catch(() => {
            if (seq !== requestSeq.current) return
            updateDataTolerancias([])
            setLoadingTolerancias(false)
          })
      } catch {
        setLoadingTolerancias(false)
      }
    },
    [handleStorange, updateDataTolerancias, updatePagination, pagination]
  )

  const createTolerancia = useCallback(
    async (obj: any) => {
      let success = false
      setLoadingTolerancias(true)
      try {
        await ParametroCompra.criarTolerancias(obj)
        success = true

        toast({
          title: 'Sucesso.',
          description: 'Nova Tolerância cadastrada com Sucesso',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
      } catch (error: any) {
        const message =
          error.response?.status === 400
            ? error.response?.data?.message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
        setLoadingTolerancias(false)
      }
      setSelectedTolerancia(0)
      getDataTolerancias(companieId)
      // Modal só fecha no sucesso
      return success
    },
    [getDataTolerancias, companieId, toast]
  )

  const handleSelectTolerancia = useCallback(
    async (index: any) => {
      const similarProduct = index['id-id']
      const newSelected = selectedTolerancia

      if (newSelected === similarProduct) {
        setSelectedTolerancia(0)
        setUpdateToleranciaOb(null)
      } else {
        setSelectedTolerancia(similarProduct)

        const dataOne = dataTolerancias.filter(
          (x: { id: any }) => x.id === similarProduct
        )[0]
        setUpdateToleranciaOb(dataOne)
      }
    },
    [dataTolerancias, selectedTolerancia, setUpdateToleranciaOb]
  )

  const deleteDataTolerancia = useCallback(async () => {
    setLoadingTolerancias(true)
    try {
      await ParametroCompra.removerTolerancias(selectedTolerancia)
      setUpdateToleranciaOb(null)

      toast({
        title: 'Sucesso.',
        description: 'Registro deletado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
      getDataTolerancias(companieId)
      setSelectedTolerancia(0)
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoadingTolerancias(false)
      getDataTolerancias(companieId)
      setSelectedTolerancia(0)
    }
  }, [
    selectedTolerancia,
    setUpdateToleranciaOb,
    toast,
    getDataTolerancias,
    companieId
  ])

  const editDataTolerancia = useCallback(
    async (obj: any) => {
      let success = false
      setLoadingTolerancias(true)

      try {
        await ParametroCompra.editarTolerancias(obj)
        success = true
        toast({
          title: 'Sucesso.',
          description: 'Parametro Compra - Tolerância editado com Sucesso',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        setSelectedTolerancia(0)
      } catch (error: any) {
        const message =
          error.response?.status === 400
            ? error.response?.data?.message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
        setLoadingTolerancias(false)
      }
      if (success) {
        setSelectedTolerancia(0)
        // Evita que Editar reabra com os dados antigos
        setUpdateToleranciaOb(null)
      }
      getDataTolerancias(companieId)
      return success
    },
    [getDataTolerancias, companieId, toast, setUpdateToleranciaOb]
  )

  const tableInteractionTolerancia = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: null,
        onChange: handleSelectTolerancia,
        selecteds: selectedTolerancia,
        isMulti: false
      }
    ]
  }, [handleSelectTolerancia, selectedTolerancia])

  const actionsTolerancia = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'csv',
            entity: 'purchaseToleranciaData'
          }
        ]
      },
      {
        type: 'purchaseToleranciaHistoric',
        data: dataTolerancias,
        onUpdate: updateDataTolerancias
      }
    ]
  }, [dataTolerancias, updateDataTolerancias])

  const handleCompanieId = useCallback(
    async (companieId: any) => {
      setCompanieId(companieId.value)
      getDataTolerancias(companieId.value)
    },
    [getDataTolerancias]
  )

  return (
    <ParametrizationConfigToleranciaContext.Provider
      value={{
        loading,
        companieId,
        getDataTolerancias,
        dataTolerancias: parseConfigTolerancia(dataTolerancias),
        loadingTolerancias,
        createTolerancia,
        updateToleranciaOb,
        selectedTolerancia,
        handleSelectTolerancia,
        tableInteractionTolerancia,
        deleteDataTolerancia,
        editDataTolerancia,
        actionsTolerancia,
        handleCompanieId,
        updatePagination,
        pagination,
        // Missing properties from IConfigToleranciaParamProvider
        loadingEdit: false,
        data: [],
        params: {} as any, // Placeholder, adjust type as needed
        paramsEdit: {} as any, // Placeholder, adjust type as needed
        paramsDates: {} as any, // Placeholder, adjust type as needed
        tooglesTable: {} as any, // Placeholder, adjust type as needed
        fastQuery: '',
        dataOriginal: [],
        tableInteraction: [],
        query,
        hiddenColumns: [],
        filename,
        filters: false,
        headers: [],
        filtersList: [],
        fetching,
        actions: [],
        selected: 0,
        filterEdit: {} as any, // Placeholder, adjust type as needed
        defaultDate: {} as any, // Placeholder, adjust type as needed
        currentyDate: {} as any, // Placeholder, adjust type as needed
        getData: () => {},
        handlePageChange: () => {},
        setQuery,
        setFilename,
        toggleColumn: () => {},
        handleToogleTable: () => {},
        handleParamsDate: () => {},
        handleUpdateParams: () => {},
        handleUpdateEditParams: () => {},
        handleSelect: () => {},
        createData: () => {},
        updateDataCurve: () => {},
        exportData: () => {},
        deleteData: () => {},
        setFilters: { on: () => {}, off: () => {}, toggle: () => {} },
        getCenters: () => {},
        handleSelectAll: () => {},
        selecteds: [],
        pageCurrent: 0,
        total: 0,
        clearAllFilters: () => {},
        clearForm: () => {},
        activeTimeline: 0,
        dateInitial: '',
        dateFinal: '',
        initialSchedule: {},
        onScheduleChange: () => {},
        initialScheduleSpecial: {},
        onScheduleChangeSpecial: () => {},
        saveSchedules: () => {},
        setActiveTimeLine: () => {},
        dataUser: [],
        loadingUser: false,
        getDataUsers: () => {},
        createUsers: () => {},
        selectedUser: 0,
        updateUserOb: [],
        handleSelectUser: () => {},
        tableInteractionUser: [],
        deleteDataUser: () => {},
        editDataUser: () => {},
        actionsUser: [],
        dataFerias: [],
        loadingFerias: false,
        getDataFerias: () => {},
        createFerias: () => {},
        selectedFerias: 0,
        updateFeriasOb: null,
        handleSelectFerias: () => {},
        tableInteractionFerias: [],
        deleteDataFerias: () => {},
        editDataFerias: () => {},
        actionsFerias: [],
        getCronogramaCompra: () => {},
        cronogramaCompra: [],
        createCronogramaCompra: () => {},
        handleCronogromaCompra: () => {},
        deleteCronogromaCompra: () => {},
        loadingCronograma: false
      }}
    >
      {children}
    </ParametrizationConfigToleranciaContext.Provider>
  )
}

export default ParametrizationConfigToleranciaProvider
export const ParametrizationConfigToleranciaContext =
  createContext<IConfigToleranciaParamProvider>(
    {} as IConfigToleranciaParamProvider
  )
